import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** One switchable staff mailbox, as returned by GET /gmail/auth/staff-mailboxes. */
export interface StaffMailbox {
  id: number;
  name: string;
  email: string;
  isSelf: boolean;
  isConnected: boolean;
}

/**
 * Holds the owner's currently-impersonated staff mailbox (the "switch account"
 * target). When set, the EmailImpersonationInterceptor stamps every Gmail
 * request with `X-Impersonate-User-Id`, so the whole mailbox operates as that
 * rep.
 *
 * The target is persisted in sessionStorage because switching a mailbox does a
 * full reload — `reloadIntoMailbox()`, the same pattern the own-account switcher
 * uses, and see it for why that reload must drop the URL's `?thread=` — and a
 * hard reload wipes in-memory state, so the choice is rehydrated on boot. sessionStorage (not localStorage) keeps it tab-scoped and gone once the
 * tab closes, which suits impersonation.
 */
@Injectable({ providedIn: 'root' })
export class EmailImpersonationService {
  private static readonly STORAGE_KEY = 'email_impersonation_target';

  private readonly target$ = new BehaviorSubject<StaffMailbox | null>(this.restore());
  /** Emits the active target (or null when the owner is in their own mailbox). */
  readonly current$: Observable<StaffMailbox | null> = this.target$.asObservable();

  /** The active target, read synchronously (used by the interceptor). */
  get target(): StaffMailbox | null {
    return this.target$.getValue();
  }

  /** The impersonated backend user's id, or null when not impersonating. */
  get targetId(): number | null {
    return this.target$.getValue()?.id ?? null;
  }

  get isImpersonating(): boolean {
    return this.target$.getValue() != null;
  }

  /** Switch into a rep's mailbox. Callers typically reload the mailbox after. */
  setTarget(mailbox: StaffMailbox): void {
    try {
      sessionStorage.setItem(EmailImpersonationService.STORAGE_KEY, JSON.stringify(mailbox));
    } catch {
      /* sessionStorage may be unavailable (private mode) — keep the in-memory value */
    }
    this.target$.next(mailbox);
  }

  /** Return to the owner's own mailbox. */
  clear(): void {
    try {
      sessionStorage.removeItem(EmailImpersonationService.STORAGE_KEY);
    } catch {
      /* ignore */
    }
    this.target$.next(null);
  }

  private restore(): StaffMailbox | null {
    try {
      const raw = sessionStorage.getItem(EmailImpersonationService.STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed.id === 'number' ? (parsed as StaffMailbox) : null;
    } catch {
      return null;
    }
  }
}
